import {
  Category,
  Classification,
  ColumnFamily,
  ComplimentaryFieldMap,
  DataRetentionPeriod,
  EnumValue,
  InCompatibleField,
  ReportingFieldDataType,
  RequiredField
} from '@flexible-reports/store/metadata.types';

export interface FlexibleMetadata {
  dimCategories: Category[];
  dimFamilies: ColumnFamily[];
  metricCategories: Category[];
  metricFamilies: ColumnFamily[];
  inCompatibleFields: InCompatibleField[];
  requiredFields: RequiredField[];
  complimentaryFieldsMap: ComplimentaryFieldMap;
  fieldNamesById: { [key: string]: string };
  metricAttrModelById: { [key: string]: string };
  deprecatedIdsMap: { [key: string]: string };
}

// Ads Domain Model
// https://domain-model.tada.amazon.dev/tree?entity=ReportingField

export interface ReportingField {
  readonly reportingFieldId: string;
  readonly deprecatedIds?: string[] | null;
  readonly name: string;
  readonly definition: string;
  readonly type: FieldType;
  readonly isFilterable: boolean;
  readonly fieldCompatibility: ReportingFieldCompatibility[];
  readonly fieldDetails: ReportingFieldDetails;
  readonly displayOrder?: number;
  readonly enumValues?: EnumValue[];
  readonly dataType?: ReportingFieldDataType;
  readonly classifications?: Classification[] | null;
}

export interface ReportingFieldCompatibility {
  readonly compatibilityType: CompatibilityType;
  readonly reportingFieldIds: string[];
}

export interface ReportingFieldDetails {
  readonly dimensionFieldDetails?: DimensionFieldDetails;
  readonly metricFieldDetails?: MetricFieldDetails;
}

export interface DimensionFieldDetails {
  readonly dataRetentionPeriod?: DataRetentionPeriod;
  readonly enumValues?: EnumValue[];
  readonly dimensionCategory: DimensionCategory;
  readonly dimension: Dimension;
  readonly dimensionAttribute?: DimensionAttribute;
}

export interface DimensionCategory {
  readonly dimensionCategory: string;
  readonly name: string;
  readonly displayOrder: number;
}

export interface Dimension {
  readonly dimension: string;
  readonly name: string;
  readonly dimensionCategory?: DimensionCategory;
  readonly displayOrder: number;
}

export interface DimensionAttribute {
  readonly dimensionAttribute: string;
  readonly name: string;
  readonly dimension?: Dimension;
  readonly displayOrder: number;
}

export interface MetricFieldDetails {
  readonly metricCategory: MetricCategory;
  readonly metricFamily?: MetricFamily;
  readonly metricAttrSource?: AttributionSource | null;
  readonly metricAttrScope?: AttributionScope | null;
  readonly metricAttrLookback?: AttributionLookbackWindow | null;
  readonly metricAttrModel?: AttributionModel | null;
}

export interface MetricCategory {
  readonly metricCategory: string;
  readonly name: string;
  readonly displayOrder: number;
}

export interface MetricFamily {
  readonly metricFamily?: string;
  readonly name?: string;
  readonly metricCategory?: MetricCategory;
  readonly displayOrder?: number;
}

export enum CompatibilityType {
  COMPATIBLE = 'COMPATIBLE',
  INCOMPATIBLE = 'INCOMPATIBLE',
  REQUIRED = 'REQUIRED',
  COMPLEMENTARY = 'COMPLEMENTARY'
}

export enum FieldType {
  DIMENSION_ATTRIBUTE = 'DIMENSION_ATTRIBUTE',
  METRIC = 'METRIC'
}

export enum ReportingInterface {
  REPORT = 'REPORT'
}

export enum AttributionSource {
  Clicks = 'Clicks',
  Views = 'Views'
}

export enum AttributionScope {
  Promoted = 'Promoted',
  Halo = 'Halo'
}

export enum AttributionLookbackWindow {
  SEVEN_DAYS = 'SEVEN_DAYS',
  FOURTEEN_DAYS = 'FOURTEEN_DAYS'
}

//ToDo: clean up legacy enums after metadata API is updated https://i.amazon.com/issues/RCMRX-11364
export enum AttributionModel {
  LAST_TOUCH_LEGACY = 'Last touch',
  MULTI_TOUCH_LEGACY = 'Multi-touch',
  LAST_TOUCH = 'LAST_TOUCH',
  MULTI_TOUCH = 'MULTI_TOUCH'
}
